import { Brand, FilterOption, Country } from "@features/events/types";

export class BrandFilterOptionsBuilder {
  private brands: Brand[];
  private allCountries: Country[];
  private roleLabels: { [key: string]: string };

  constructor(
    brands: Brand[],
    allCountries: Country[],
    roleLabels: { [key: string]: string }
  ) {
    this.brands = brands;
    this.allCountries = allCountries;
    this.roleLabels = roleLabels;
  }

  /**
   * Get role options based on brands that actually have those roles
   */
  getRoleOptions(): FilterOption[] {
    // Extract unique roles from brands
    const uniqueRoles = new Set<string>();
    
    this.brands.forEach((brand) => {
      if (brand.role) {
        uniqueRoles.add(brand.role.toLowerCase());
      }
    });

    // Map to role choices
    return Array.from(uniqueRoles)
      .map((role) => ({
        label: this.roleLabels[role] || role.charAt(0).toUpperCase() + role.slice(1),
        value: role,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Get country options based on brands that actually have those countries
   */
  getCountryOptions(): FilterOption[] {
    // Extract unique country ISO3 codes from brands
    const uniqueCountryISO3s = new Set<string>();
    
    this.brands.forEach((brand) => {
      if (brand.geoAddress?.countryISO3) {
        uniqueCountryISO3s.add(brand.geoAddress.countryISO3);
      }
    });

    // Map to country choices and filter to only include countries present in brands
    return this.allCountries
      .filter((country) => uniqueCountryISO3s.has(country.iso3))
      .map((country) => ({
        label: country.name,
        value: country.iso3,
        flag: country.svg,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /**
   * Get all filter options at once
   */
  getAllOptions(): {
    roles: FilterOption[];
    countries: FilterOption[];
  } {
    return {
      roles: this.getRoleOptions(),
      countries: this.getCountryOptions(),
    };
  }
}

